/**
 * A medication's dose slots for one day in Medication Administration, from
 * keyon's time_status_pairs ("time||takentime||status||reason", one per slot,
 * in order; a slot not given yet has an empty taken time).
 *
 * Each slot takes its taken time from its own pair. Copying the day's non-empty
 * taken times onto the slots by position put a later dose's time on an earlier
 * slot once one was skipped (19:00 given at 18:29 showed on 08:00 and locked it).
 * A slot with a taken time but no status reads as taken.
 */

const LOCKED = ["taken", "hold", "hold-medtime", "discontinue", "discontinue-medtime"];

export function isLockedStatus(status) {
  return LOCKED.includes(status);
}

export function parseTimeStatusPairs(pairsStr) {
  if (!pairsStr) return [];
  // If already an array (e.g. server returned JSON), coerce to plain array
  if (Array.isArray(pairsStr)) return pairsStr;

  // If it's a Vue ref, unwrap
  if (typeof pairsStr === "object" && Object.prototype.hasOwnProperty.call(pairsStr, "value")) {
    return parseTimeStatusPairs(pairsStr.value);
  }

  if (typeof pairsStr === "string") {
    try {
      const parsed = JSON.parse(pairsStr);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) { /* not JSON, continue */ }

    // expected token format: "time||takentime||status||reason, time2||..."
    return pairsStr
      .split(",")
      .map((s) => s.trim())
      .map((entry) => {
        const parts = entry.split("||").map((p) => (p ?? "").trim());
        return {
          time: parts[0] ?? "",
          takentime: parts[1] ?? "",
          status: parts[2] ?? "",
          reason: parts[3] ?? "",
        };
      })
      // drop entries where every field is empty ("|||")
      .filter((obj) => obj.time || obj.takentime || obj.status || obj.reason);
  }

  // An object containing an array field like { pairs: [...] }
  if (typeof pairsStr === "object") {
    if (Array.isArray(pairsStr.pairs)) return pairsStr.pairs;
    if (Array.isArray(pairsStr.data)) return pairsStr.data;
  }

  return [];
}

/** One entry per pair, in order: display time, status, lock, the slot's own taken time. */
export function daySlotsFromPairs(pairs, dosage) {
  return pairs.map((p) => {
    let displayTime = p.time;
    if (p.takentime) displayTime = `${p.time} (taken at ${p.takentime})`;
    if (p.reason) displayTime += ` (${p.reason})`;

    const status = p.status || (p.takentime ? "taken" : "");
    return {
      time: displayTime,
      rawTime: p.time,
      status,
      takentime: p.takentime || "",
      dosage,
      earlyReason: p.reason || "",
      locked: isLockedStatus(status),
      temporaryStatus: status,
      tagstatus: p.tagstatus,
    };
  });
}

const HELD = ["hold", "hold-medtime"];

function holdDates(value) {
  return String(value || "")
    .split(",")
    .map((d) => d.trim())
    .filter((d) => /^\d{4}-\d{2}-\d{2}/.test(d))
    .map((d) => d.slice(0, 10));
}

/**
 * The edit form's hold label: the held dose times from the row's
 * own slots and the hold's date span - "Held 08:00, 19:00 · 10-12 → 10-14".
 * It showed formData.heldtime, the medication log row's write time, next to the
 * raw status ("18:55:55 (status:hold-medtime)"). medholdstartdate / enddate come
 * as a GROUP_CONCAT over the day's slots, so they can repeat.
 */
export function heldSlotLabel(med) {
  const m = med || {};
  const times = [...new Set(parseTimeStatusPairs(m.time_status_pairs)
    .filter((p) => HELD.includes(p.status))
    .map((p) => p.time))];
  const dates = [...holdDates(m.medholdstartdate), ...holdDates(m.medholdenddate)].sort();
  const md = (d) => d.slice(5);
  let span = "";
  if (dates.length) {
    const first = dates[0];
    const last = dates[dates.length - 1];
    span = first === last ? md(first) : `${md(first)} → ${md(last)}`;
  }
  return ["Held" + (times.length ? " " + times.join(", ") : ""), span].filter(Boolean).join(" · ");
}
